import { ITextBlockDataAttr, } from '@nedoShared/modules/notes/block/textBlock/TextBlock.types';
const isElement = (node) => {
    if (node.nodeType === Node.ELEMENT_NODE) {
        const element = node;
        return (element.hasAttribute(ITextBlockDataAttr.TEXT) ||
            element.hasAttribute(ITextBlockDataAttr.INPUT) ||
            element.hasAttribute(ITextBlockDataAttr.PLACEHOLDER));
    }
    return false;
};
const getElementType = (element) => {
    return element.hasAttribute(ITextBlockDataAttr.TEXT)
        ? ITextBlockDataAttr.TEXT
        : element.hasAttribute(ITextBlockDataAttr.INPUT)
            ? ITextBlockDataAttr.INPUT
            : ITextBlockDataAttr.PLACEHOLDER;
};
const getParentElement = (node, container) => {
    let parentNode = node.parentNode;
    while (parentNode !== container) {
        if (isElement(parentNode)) {
            const element = parentNode;
            return { element: element, type: getElementType(element) };
        }
        parentNode = parentNode.parentNode;
    }
    return null;
};
const getElement = (node, container) => {
    if (!container.contains(node)) {
        return null;
    }
    else if (node.nodeType === Node.TEXT_NODE) {
        return getParentElement(node, container);
    }
    const element = node.nodeType === Node.ELEMENT_NODE ? node : null;
    if (element && element.hasAttribute(ITextBlockDataAttr.TEXT)) {
        return { element: element, type: ITextBlockDataAttr.TEXT };
    }
    else if (element && element.hasAttribute(ITextBlockDataAttr.INPUT)) {
        return { element: element, type: ITextBlockDataAttr.INPUT };
    }
    else if (element && element.hasAttribute(ITextBlockDataAttr.PLACEHOLDER)) {
        return { element: element, type: ITextBlockDataAttr.PLACEHOLDER };
    }
    else if (element) {
        const textElement = element.querySelector(`[${ITextBlockDataAttr.TEXT}="true"]`);
        const inputElement = element.querySelector(`[${ITextBlockDataAttr.INPUT}="true"]`);
        const placeholderElement = element.querySelector(`[${ITextBlockDataAttr.PLACEHOLDER}="true"]`);
        const elements = [textElement, placeholderElement, inputElement].filter((item) => !!item);
        if (elements.length > 1) {
            return null;
        }
        else if (elements.length === 1) {
            const element = elements[0];
            return { element: element, type: getElementType(element) };
        }
        else if (elements.length < 1) {
            return getParentElement(node, container);
        }
    }
    return null;
};
const getTextElement = (node, container) => {
    if (node.nodeType === Node.ELEMENT_NODE &&
        node.hasAttribute(ITextBlockDataAttr.TEXT)) {
        return node;
    }
    else if (node.nodeType === Node.TEXT_NODE) {
        const element = getParentElement(node, container);
        return element && element.type === ITextBlockDataAttr.TEXT ? element.element : null;
    }
    else if (node.nodeType === Node.ELEMENT_NODE && container.contains(node)) {
        const textElement = node.querySelector(`[${ITextBlockDataAttr.TEXT}="true"]`);
        return textElement;
    }
    return null;
};
const getInputElement = (node, container) => {
    if (node.nodeType === Node.ELEMENT_NODE &&
        node.hasAttribute(ITextBlockDataAttr.INPUT)) {
        return node;
    }
    else if (node.nodeType === Node.TEXT_NODE) {
        const element = getParentElement(node, container);
        return element && element.type === ITextBlockDataAttr.INPUT ? element.element : null;
    }
    else if (node.nodeType === Node.ELEMENT_NODE && container.contains(node)) {
        const inputElement = node.querySelector(`[${ITextBlockDataAttr.INPUT}="true"]`);
        return inputElement;
    }
    return null;
};
const getPlaceholderElement = (node, container) => {
    if (node.nodeType === Node.ELEMENT_NODE &&
        node.hasAttribute(ITextBlockDataAttr.PLACEHOLDER)) {
        return node;
    }
    else if (node.nodeType === Node.TEXT_NODE) {
        const element = getParentElement(node, container);
        return element && element.type === ITextBlockDataAttr.PLACEHOLDER ? element.element : null;
    }
    else if (node.nodeType === Node.ELEMENT_NODE && container.contains(node)) {
        const element = node;
        const placeholder = element.querySelector(`[${ITextBlockDataAttr.PLACEHOLDER}="true"]`);
        return placeholder;
    }
    return null;
};
const getCaretPosition = (container) => {
    const selection = window.getSelection();
    if (!selection.rangeCount)
        return null;
    const range = selection.getRangeAt(0);
    const result = {
        node: range.startContainer,
        offset: range.startOffset,
        element: null,
    };
    const element = getElement(range.startContainer, container);
    if (element) {
        result.element = element;
    }
    return result;
};
const setCaretPosition = (node, offset) => {
    var _a;
    const selection = window.getSelection();
    const range = document.createRange();
    try {
        if (node.nodeType === Node.ELEMENT_NODE &&
            ((_a = node.firstChild) === null || _a === void 0 ? void 0 : _a.nodeType) === Node.TEXT_NODE &&
            node.firstChild.textContent.length >= offset) {
            node = node.firstChild;
        }
        range.setStart(node, offset > node.textContent.length ? node.textContent.length : offset);
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);
    }
    catch (e) {
        console.error('Error setting caret position:', e);
    }
};
const focusElement = (containerElement, { index, offset }) => {
    setTimeout(() => {
        const childNodes = Array.from(containerElement.childNodes);
        const node = childNodes[index];
        if (node) {
            const element = getElement(node, containerElement);
            if (element &&
                (element.type === ITextBlockDataAttr.TEXT || element.type === ITextBlockDataAttr.INPUT)) {
                setCaretPosition(element.element, offset);
                return;
            }
        }
    }, 0);
};
const getSelectionOnly = () => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0)
        return null;
    const range = selection.getRangeAt(0);
    let startNode = range.startContainer; // The node where selection starts
    let endNode = range.endContainer; // The node where selection ends
    let startOffset = range.startOffset; // Character offset in startNode
    let endOffset = range.endOffset; // Character offset in endNode
    return {
        startNode,
        endNode,
        startOffset,
        endOffset,
        originalSelection: selection,
    };
};
const getSelection = (container) => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0)
        return null;
    const range = selection.getRangeAt(0);
    let startNode = range.startContainer; // The node where selection starts
    let endNode = range.endContainer; // The node where selection ends
    let startOffset = range.startOffset; // Character offset in startNode
    let endOffset = range.endOffset; // Character offset in endNode
    const startElement = getElement(startNode, container);
    if (startElement &&
        (startElement.type === ITextBlockDataAttr.TEXT ||
            startElement.type === ITextBlockDataAttr.INPUT)) {
        startNode = startElement.element;
    }
    else if (startElement && startElement.type === ITextBlockDataAttr.PLACEHOLDER) {
        let nextSibling = startElement.element.nextSibling;
        while (nextSibling) {
            const textElement = getTextElement(nextSibling, container);
            if (textElement) {
                startNode = textElement;
                startOffset = 0;
                break;
            }
            nextSibling = nextSibling.nextSibling;
        }
    }
    const endElement = getElement(endNode, container);
    if (endElement &&
        (endElement.type === ITextBlockDataAttr.TEXT || endElement.type === ITextBlockDataAttr.INPUT)) {
        endNode = endElement.element;
    }
    else if (endElement && endElement.type === ITextBlockDataAttr.PLACEHOLDER) {
        let prevSibling = endElement.element.previousSibling;
        while (prevSibling) {
            const textElement = getTextElement(prevSibling, container);
            if (textElement) {
                endNode = textElement;
                endOffset = textElement.textContent.length;
                break;
            }
            prevSibling = prevSibling.previousSibling;
        }
    }
    return {
        startNode,
        endNode,
        startOffset,
        endOffset,
        originalSelection: selection,
    };
};
const selectAllContent = (element) => {
    const selection = window.getSelection();
    const range = document.createRange();
    const firstElement = element.firstChild;
    const lastElement = element.lastChild;
    if (firstElement && lastElement && firstElement === lastElement) {
        range.selectNodeContents(firstElement.childNodes[0]);
        selection === null || selection === void 0 ? void 0 : selection.removeAllRanges();
        selection === null || selection === void 0 ? void 0 : selection.addRange(range);
    }
    else if (firstElement && lastElement) {
        const startNode = firstElement.childNodes[0];
        const endNode = lastElement.childNodes[lastElement.childNodes.length - 1];
        range.setStart(startNode, 0);
        range.setEnd(endNode, endNode.textContent.length);
        selection === null || selection === void 0 ? void 0 : selection.removeAllRanges();
        selection === null || selection === void 0 ? void 0 : selection.addRange(range);
    }
};
const alertWithConsoleLogging = (message, ...rest) => {
    // alert(message)
    console.log(message, ...rest);
};
const observeTextChanges = (element, callback) => {
    const observer = new MutationObserver((mutations, observer) => {
        for (const mutation of mutations) {
            if (mutation.type === 'characterData' ||
                mutation.type === 'childList' ||
                // @ts-ignore
                mutation.type === 'subtree') {
                callback(observer);
                break;
            }
        }
    });
    observer.observe(element, {
        characterData: true,
        childList: true,
        subtree: true,
    });
    return () => observer.disconnect();
};
class TextBlockDomHelpers {
}
// Selection and cursor(caret)
TextBlockDomHelpers.getCaretPosition = getCaretPosition;
TextBlockDomHelpers.setCaretPosition = setCaretPosition;
TextBlockDomHelpers.getSelection = getSelection;
TextBlockDomHelpers.getSelectionOnly = getSelectionOnly;
TextBlockDomHelpers.selectAllContent = selectAllContent;
// Getting  HTML representation of ITextBlockElement
TextBlockDomHelpers.getTextElement = getTextElement;
TextBlockDomHelpers.getInputElement = getInputElement;
TextBlockDomHelpers.getPlaceholderElement = getPlaceholderElement;
TextBlockDomHelpers.getElement = getElement;
// Focus to HTML representation of ITextBlockElement
TextBlockDomHelpers.focusElement = focusElement;
TextBlockDomHelpers.observeTextChanges = observeTextChanges;
// Show error
TextBlockDomHelpers.alertWithConsoleLogging = alertWithConsoleLogging;
export default TextBlockDomHelpers;
